import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  Query,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';

import { ThemeProvider } from '@kognitos/lattice';
import { AuthProvider, AxiosInterceptorProvider } from '@/shared/auth';
import { ConfigProvider } from '@/config/ConfigProvider';
import {
  checkIsAccessDeniedError,
  checkIsNotFoundError,
} from '@/shared/utils/error-handler';
import { StatsigProviderWrapper } from './StatsigProvider';
import { NetworkConnectivityMonitor } from '@/shared/connectivity';
import { FinanceAuthBridge } from '@/modules/finance/api/finance-auth-bridge';
import { IntercomProvider } from './IntercomProvider';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) =>
        // 403/404 are terminal; retrying only delays the real error UI.
        !checkIsAccessDeniedError(error) &&
        !checkIsNotFoundError(error) &&
        failureCount < 3,
    },
  },
  queryCache: new QueryCache({
    // When you're using meta.onSuccess or meta.onError,
    // Make sure you always set refetchOnMount to 'always'
    // Otherwise, this query will not call onSuccess or onError
    // As, these will be only called on API success,
    // not on generated Hook's initial call
    // These will help us to avoid using an extra
    // `useEffect` to call the onSuccess or onError
    onSuccess: (
      data: unknown,
      query: Query<unknown, unknown, unknown, readonly unknown[]>,
    ) => {
      if (query.meta?.onSuccess && typeof query.meta.onSuccess === 'function') {
        query.meta.onSuccess(data, query);
      }
    },
    onError: (
      error: unknown,
      query: Query<unknown, unknown, unknown, readonly unknown[]>,
    ) => {
      if (query.meta?.onError && typeof query.meta.onError === 'function') {
        query.meta.onError(error, query);
      }
    },
  }),
});

declare global {
  interface Window {
    __TANSTACK_QUERY_CLIENT__: QueryClient;
  }
}

window.__TANSTACK_QUERY_CLIENT__ = queryClient;

export const Providers = ({ children }: { children: React.ReactNode }) => {
  return (
    <ConfigProvider>
      <AuthProvider>
        <StatsigProviderWrapper>
          <AxiosInterceptorProvider>
            <QueryClientProvider client={queryClient}>
              <ReactQueryDevtools
                buttonPosition="bottom-right"
                initialIsOpen={false}
              />
              <ThemeProvider defaultTheme="dark" storageKey="kognitos-ui-theme">
                <IntercomProvider>
                  <NetworkConnectivityMonitor />
                  <FinanceAuthBridge />
                  {children}
                </IntercomProvider>
              </ThemeProvider>
            </QueryClientProvider>
          </AxiosInterceptorProvider>
        </StatsigProviderWrapper>
      </AuthProvider>
    </ConfigProvider>
  );
};
